import React from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import AirIcon from '@mui/icons-material/Air';
import CloudIcon from '@mui/icons-material/Cloud';
import OpacityIcon from '@mui/icons-material/Opacity';
import SpeedIcon from '@mui/icons-material/Speed';
import { useTranslation } from 'react-i18next';
import { useWeather } from '../../hooks/useWeather';
import WeatherChart from './WeatherChart';

interface Props {
  latitude?: number | null;
  longitude?: number | null;
  locationName?: string;
}

const formatValue = (value: number | null | undefined, suffix: string) => (typeof value === 'number' ? `${value}${suffix}` : '-');

const getConditionKey = (condition: string) => condition.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

const WeatherCard: React.FC<Props> = ({ latitude, longitude, locationName }) => {
  const { t } = useTranslation();
  const { current, weeklyForecast, loading, error, reload } = useWeather(latitude, longitude);
  const hasCoordinates = typeof latitude === 'number' && typeof longitude === 'number';
  const weatherText = (key: string, defaultValue: string) => t(`dashboard.weather.${key}`, { defaultValue });

  return (
    <div className="weather-dashboard-card">
      <div className="weather-card-header">
        <div>
          <span className="weather-eyebrow">{weatherText('title', 'Weather')}</span>
          <h2>{locationName || weatherText('companyLocation', 'Company Location')}</h2>
        </div>
        <button
          type="button"
          className="weather-refresh-btn"
          onClick={() => reload()}
          aria-label={weatherText('reload', 'Reload weather')}
        >
          <RefreshIcon fontSize="small" />
        </button>
      </div>

      {!hasCoordinates ? (
        <div className="weather-empty-state">{weatherText('noForecast', 'No forecast available.')}</div>
      ) : loading && !current ? (
        <div className="weather-empty-state">{weatherText('loading', 'Loading weather...')}</div>
      ) : error ? (
        <div className="weather-empty-state">{weatherText('loadError', 'Unable to load weather.')}</div>
      ) : current ? (
        <>
          <div className="weather-current">
            <div className="weather-condition-icon">
              <WbSunnyIcon fontSize="large" />
            </div>
            <div>
              <div className="weather-temp">{formatValue(current.temperature, ' C')}</div>
              <div className="weather-condition">
                {t(`dashboard.weather.conditions.${getConditionKey(current.condition)}`, { defaultValue: current.condition })}
              </div>
            </div>
          </div>

          <div className="weather-facts">
            <div>
              <AirIcon fontSize="small" />
              <span>{weatherText('metrics.wind', 'Wind')}</span>
              <strong>{formatValue(current.windSpeed, ' km/h')}</strong>
            </div>
            <div>
              <OpacityIcon fontSize="small" />
              <span>{weatherText('metrics.humidity', 'Humidity')}</span>
              <strong>{formatValue(current.humidity, '%')}</strong>
            </div>
            <div>
              <OpacityIcon fontSize="small" />
              <span>{weatherText('metrics.rain', 'Rain')}</span>
              <strong>{formatValue(current.rain ?? current.precipitation, ' mm')}</strong>
            </div>
            <div>
              <SpeedIcon fontSize="small" />
              <span>{weatherText('metrics.pressure', 'Pressure')}</span>
              <strong>{formatValue(current.pressure, ' hPa')}</strong>
            </div>
            <div>
              <CloudIcon fontSize="small" />
              <span>{weatherText('metrics.clouds', 'Clouds')}</span>
              <strong>{formatValue(current.cloudCover, '%')}</strong>
            </div>
          </div>

          <WeatherChart forecast={weeklyForecast} />
        </>
      ) : null}
    </div>
  );
};

export default WeatherCard;
